
import { Component, Prop, Provide, ProvideReactive, Vue, Watch, Ref } from "vue-property-decorator"
import { Getter, namespace } from "vuex-class"

import { CasesPortalNames } from "@/views/new/Cases.vue"

import Header from "@/components/New/Cases/EditingCase/Header.vue"
import CaseToolbar from "./EditingCase/CaseToolbar.vue"
import Desktop from "./EditingCase/Desktop.vue"
import UserImage from "@/components/UserImage.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import CaseSubheader from "./EditingCase/CaseSubheader.vue"

import { CaseDetails as CaseDetailsType, TemplateDetails, Area } from "@/types"

import HubCaseService from "@/services/hubCaseService"

import api from "@/services/api"
import eventsApi from "@/services/api.event"
import { CurrentUser } from "@/types/user"
import { CaseEvents } from "@/types/originatingEvents"
import { RecordFieldsUpdateDto } from "@/types/caseUpdate"
import { applyTemplate, getCanEditClosedCase, getCaseHasMapField, updateCaseFields } from "@/scripts/caseDetails"
import FileService from "@/services/file-service"
import { MapProvider } from "@/services/mapProvider"
import CaseFile from "@/types/caseFile"
import { TenantFeatures } from "@/types/tenantFeatures"
import { UserSettings } from "@/types/userSettings"
import CaseHubEventHandler from "./EditingCase/CaseHubEventHandler.vue"
import CaseAuditHistoryService from "@/services/caseAuditHistoryService"

const caseStore = namespace("cases")

@Component({
	components: {
		"skeleton": SkeletonLoader,
		"case-header": Header,
		"toolbar": CaseToolbar,
		"desktop": Desktop,
		"user-image": UserImage,
		"subheader": CaseSubheader,
		"case-hub-event-handler": CaseHubEventHandler
	}
})
export default class CaseDetails extends Vue {
	@ProvideReactive("hubCaseService") hubCaseService: HubCaseService = new HubCaseService()
	@Provide() caseAuditHistoryService: CaseAuditHistoryService = new CaseAuditHistoryService()

	@Provide("mapProvider") mapProvider: MapProvider = new MapProvider()
	@Provide("fileService") fileService: FileService = new FileService(this.caseFilesChanged)

	@Ref() private readonly caseHubEventHandler!: CaseHubEventHandler

	@Getter("getMe")
	public me!: CurrentUser
	@Getter isFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter getTenantSettings!: { [key: string]: any } | null

	@Getter("getUserSettings")
	public userSettings!: UserSettings

	@Getter("getCaseName") caseName!: string
	@Getter("getRecordName") recordName!: string

	@caseStore.Getter
	private getBlankCase!: () => CaseDetailsType

	@caseStore.Action
	public detailsLoad!: () => Promise<void>

	@caseStore.Action
	public fetchAreas!: () => Promise<Area[]>

	@caseStore.Mutation setCurrentCase: any;

	@Prop({ default: null })
	caseNumber!: string | null

	@Prop({ default: () => null })
	createTemplateId!: string | null

	@Prop({ type: Boolean, default: false })
	borderless!: boolean

	@Prop({ type: Boolean, default: false })
	noTemplateChange!: boolean

	@Prop({ default: null })
	portalId!: string | null

	@Prop({ default: null })
	loadCase!: CaseDetailsType | null

	/* --- Constants References --- */

	private viewingUsersDisplayCount: number = 10

	/* --- Data --- */

	loading: boolean = false
	private caseLoadFailMessage: string = "";

	public currentCase: CaseDetailsType | null = null

	public currentCaseTemplate: TemplateDetails | null = null
	public caseEvents: CaseEvents | null = null
	public caseFiles: CaseFile[] = []
	public caseLoadFailed: boolean = false
	private caseFieldLastUpdatedLocally: { [fieldId: string]: Date } = {}

	editingTitle = false
	makingClosedChanges = false
	closingCase = false
	creating = false
	createdCaseNumber: string | null = null

	get footerRightPortal (): string {
		if (this.portalId) {
			return `${CasesPortalNames.FooterRight}-${this.portalId}`
		}
		return CasesPortalNames.FooterRight
	}

	get headerPortal (): string {
		if (this.portalId) {
			return `${CasesPortalNames.HeaderContent}-${this.portalId}`
		}
		return CasesPortalNames.HeaderContent
	}

	get subHeaderPortal (): string {
		if (this.portalId) {
			return `${CasesPortalNames.SubHeader}-${this.portalId}`
		}
		return CasesPortalNames.SubHeader
	}

	get canCreateCase (): boolean {
		if (!this.currentCaseTemplate) return false
		return !this.currentCaseTemplate.permissions!.case ||
			this.currentCaseTemplate.permissions!.case.edit.length === 0 ||
			this.currentCaseTemplate.permissions!.case.edit.some((e: any) => this.me.teams.some((mt: any) => mt.id === e.id))
	}

	get canCreateRecord (): boolean {
		if (!this.currentCaseTemplate || this.currentCaseTemplate.alwaysPromoted) return false
		return !this.currentCaseTemplate.permissions!.record ||
			this.currentCaseTemplate.permissions!.record.edit.length === 0 ||
			this.currentCaseTemplate.permissions!.record.edit.some((e: any) => this.me.teams.some((mt: any) => mt.id === e.id))
	}

	/**
	 * Case number to load
	 */
	get loadCaseNumber (): string | null {
		if (this.caseNumber) {
			return this.caseNumber
		}

		return this.$route && this.$route.params ? this.$route.params.caseNumber : null
	}

	get caseTitle () {
		if (this.currentCase && !this.currentCase.id &&
			this.titleTemplateEnabled) {
			return "Title set by template"
		}

		return this.currentCase?.title ?? "New Case"
	}
	set caseTitle (value: string) {
		if (this.currentCase!.title !== value) {
			this.currentCase!.title = value
		}
	}

	get canEditTitle () {
		return this.canEditCurrentCase && !!this.currentCase && (this.currentCaseTemplate || !this.currentCase.id) && !this.titleTemplateEnabled
	}

	get isTemplateLoading (): boolean {
		return !!this.currentCase?.id && !!this.currentCase.templateId && !this.currentCaseTemplate
	}

	/**
	 * Shows the title input read-only until the template loads
	 */
	get showTitleInput (): boolean {
		return !!this.canEditTitle || (this.canEditCurrentCase && this.isTemplateLoading)
	}

	get caseDeleted () {
		return !!this.currentCase?.deleted
	}

	public get caseCreationValid (): boolean {
		return !!this.currentCase &&
			!!this.currentCase.templateId &&
			(this.titleTemplateEnabled
				? true
				: (!!this.currentCase.title && this.currentCase.title.trim().length > 0))
	}

	/**
	 * Returns true if the user had Edit After Close Permissions
	 */
	public get editAfterClosePermissions (): boolean {
		return getCanEditClosedCase(this.currentCaseTemplate, this.me)
	}

	get hasEditCasePermissions () {
		if (!this.currentCase) {
			return false
		}

		if (!this.me) {
			return false
		}

		const casePermissions = this.currentCase.permissions
		if (casePermissions && casePermissions.edit && casePermissions.edit.length > 0) {
			if (!(this.me!.teams && casePermissions.edit.some(team => this.me!.teams.find((myTeam: any) => myTeam.id === team.id)))) {
				return false
			}
		}

		return true
	}

	get canEditCurrentCase () {
		if (!this.hasEditCasePermissions) {
			return false
		}

		if (this.currentCase!.isClosed) {
			return this.editAfterClosePermissions && this.makingClosedChanges
		}

		return true
	}

	get canCreateComments () {
		if (!this.hasEditCasePermissions) {
			return false
		}

		if (this.currentCase!.isClosed) {
			return this.editAfterClosePermissions
		}

		return true
	}

	private get hasMap (): boolean {
		return getCaseHasMapField(this.currentCase)
	}

	get titleTemplateEnabled (): boolean {
		return !!this.currentCaseTemplate && !!this.currentCaseTemplate.enableTitleTemplate
	}

	get viewingCount (): number {
		return this.currentCase?.viewingUsers?.length ?? 0
	}

	get viewingUsersToDisplay () {
		let result = this.currentCase?.viewingUsers ?? []

		let currentUser = result.find(user => user.id === this.me?.id)
		if (currentUser) currentUser.name += " (you)"

		// only display the first X users
		if (result.length > this.viewingUsersDisplayCount) {
			result = result.slice(0, this.viewingUsersDisplayCount)
		}

		// ensure the current user is always shown in the main list
		if (currentUser && !result.includes(currentUser) && result.length > 0) {
			result[0] = currentUser
		} else if (currentUser) {
			// make sure the current user is the first in the list
			result = [currentUser, ...result.filter(user => user.id !== this.me?.id)]
		}

		return result
	}

	get viewingUsersOverflowNames (): string {
		let result = this.currentCase?.viewingUsers ?? []

		// remove the users we are already displaying
		result = result.filter(user => !this.viewingUsersToDisplay.includes(user))

		return result.map(user => user.name).join(", ")
	}

	@Watch("loadCaseNumber")
	private async onCaseNumberChanged (newCaseNumber: string | null) {
		await this.closeCaseConnection()

		this.currentCaseTemplate = null
		this.caseEvents = null
		this.caseFiles = []
		this.caseFieldLastUpdatedLocally = {}

		this.currentCase = null
		this.setCurrentCase(null)

		this.loading = true
		this.caseLoadFailed = false
		if (newCaseNumber) {
			try {
				this.caseAuditHistoryService.clear()
				if (newCaseNumber.toLowerCase() === "new") {
					await this.detailsLoad() // This needs to be awaited to load status / severity list
					const newCase = this.getBlankCase()
					if (this.createTemplateId) {
						newCase.templateId = this.createTemplateId
					}
					this.currentCase = newCase
					this.setCurrentCase(this.currentCase)

					this.fileService.caseId = null
				} else {
					this.currentCase = await api.caseDetails(newCaseNumber)
					this.setCurrentCase(this.currentCase)
					this.caseAuditHistoryService.load(this.currentCase, { skipInitialFetch: true })
					this.fileService.caseId = this.currentCase.id!
					await Promise.all([this.detailsLoad(), this.establishCaseConnection(), this.caseFilesLoad(), this.caseEventsLoad()])
				}

				if (this.currentCase && this.currentCase.templateId) {
					this.currentCaseTemplate = await this.fetchTemplate(this.currentCase.templateId)
				}

				if ((!this.currentCase.id) && (!this.currentCaseTemplate || !this.currentCaseTemplate.enableTitleTemplate)) {
					this.editingTitle = true
				}

				// Initialize 'oldValue' property to prevent redundant Field updates on first focus loss.
				if (this.currentCase.fields) {
					Object.values(this.currentCase.fields).forEach(field => {
						if (field.oldValue === undefined) {
							field.oldValue = field.value
						}
					})
				}

				this.$emit("caseLoaded", this.currentCase)
			} catch (err) {
				console.error(err)
				this.caseLoadFailed = true
				if (err === "You do not have permission") {
					this.caseLoadFailMessage = "You do not have permission to view this " + this.caseName + "."
				} else {
					this.caseLoadFailMessage = "Unable to load requested " + this.recordName + "."
				}
			}
		}

		this.loading = false
	}

	@Watch("valid")
	onValidChanged (newValue: boolean) {
		this.$emit("validChanged", newValue)
	}

	@Watch("currentCase.templateId")
	async currentCaseTemplateIdChanged (newValue: string | null) {
		if (!newValue) {
			this.currentCaseTemplate = null
			return
		} else if (newValue === this.currentCaseTemplate?.id) {
			return
		}

		const newTemplate = await this.fetchTemplate(newValue)
		this.currentCaseTemplate = newTemplate
	}

	@Watch("currentCase.isClosed")
	private async caseIsClosedChanged (isClosed: boolean) {
		if (this.closingCase && isClosed) {
			await this.redirectAfterClose()
			this.closingCase = false
		}
	}

	async mounted () {
		const loadPromises: Promise<unknown>[] = [
			this.fetchAreas()
		]

		if (this.loadCase) {
			this.caseFieldLastUpdatedLocally = {}
			this.currentCase = this.loadCase
			this.setCurrentCase(this.currentCase)
			loadPromises.push(
				this.detailsLoad(),
				this.establishCaseConnection(),
				this.caseFilesLoad(),
				this.caseEventsLoad()
			)

			await Promise.all(loadPromises)

			if (this.currentCase.templateId) {
				this.currentCaseTemplate = await this.fetchTemplate(this.currentCase.templateId)
			}
		} else {
			await this.onCaseNumberChanged(this.loadCaseNumber)
		}
		if (this.createTemplateId) {
			await this.templateChanged(this.createTemplateId)
		}
	}

	beforeDestroy () {
		this.closeCaseConnection()
	}

	private async establishCaseConnection () {
		const caseDetails = this.currentCase

		if (caseDetails && caseDetails.id) {
			const connection = await this.hubCaseService.openConnection(caseDetails.id!)
			if (connection) {
				this.caseHubEventHandler.handleHubRecordEvents(connection)
			}
		}
	}

	private caseChanged () {
		if (this.currentCase && this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	private async closeCaseConnection () {
		if (this.hubCaseService) {
			this.hubCaseService.closeConnection()
		}
	}

	private setEvents (caseEvents: CaseEvents) {
		if (!!caseEvents && !!caseEvents.originatingEvents) {
			caseEvents.originatingEvents.forEach(event => {
				if (event.keyEntries) {
					event.keyEntries.sortBy("created")
				}
			})
		}

		this.caseEvents = caseEvents
	}

	private markFieldUpdatedLocally (fieldId: string) {
		if (fieldId) {
			this.caseFieldLastUpdatedLocally[fieldId] = new Date(Date.now())
		}
	}

	private caseFieldsUpdated (updateDetails: RecordFieldsUpdateDto) {
		if (!this.currentCase || !this.currentCase.id || updateDetails.caseId !== this.currentCase.id) {
			return
		}

		updateCaseFields(
			this.currentCase,
			updateDetails.updatedFields,
			this.me?.id ?? "",
			updateDetails.isInProgressUpdate,
			this.caseFieldLastUpdatedLocally,
			updateDetails.changedByUserId
		)
	}

	private caseNoLongerAvailable () {
		const caseNumber = this.currentCase?.caseNumber
		if (caseNumber) {
			this.$bvToast.toast(`#${caseNumber} is no longer available. You may not have access to it, or it may have been removed.`, {
				title: `Unable to load ${this.recordName}`,
				variant: "danger",
				solid: true
			})
		}

		this.currentCase = null
		this.caseEvents = null
		this.caseFiles = []
		this.setCurrentCase(null)
		this.$router.push("/cases")
	}

	private async templateChanged (templateId: string | null) {
		if (templateId) {
			this.currentCaseTemplate = await this.fetchTemplate(templateId)

			if (this.currentCase && this.currentCaseTemplate) {
				applyTemplate(this.currentCase, this.currentCaseTemplate!)

				if (!this.currentCase.id && this.currentCaseTemplate.enableTitleTemplate) {
					this.editingTitle = false
				}
			}
		} else {
			this.currentCaseTemplate = null
		}
	}

	private async fetchTemplate (templateId: string) {
		return api.templateDetails(templateId)
	}

	/**
	 * Event handler for when files on the case have been updated.
	 * If the case is being created, this will then create the case when done.
	 */
	public caseFilesChanged () {
		if (this.creating) {
			this.creating = false
			if (this.caseNumber) {
				this.$emit("caseCreated", {
					caseNumber: this.createdCaseNumber!,
					...this.currentCase
				})
			} else {
				this.$router.replace({ name: "case", params: { caseNumber: this.createdCaseNumber! } })
			}
		} else {
			this.caseFilesLoad()
		}
	}

	private async caseFilesLoad () {
		if (this.currentCase && this.currentCase.id) {
			this.caseFiles = await api.caseFiles(this.currentCase.id)
		}
	}

	private async caseEventsLoad (): Promise<void> {
		if (!this.currentCase || !this.currentCase.id) {
			return
		}

		try {
			this.setEvents(await eventsApi.caseEventsGet(this.currentCase.id))
		} catch (err) {
			// The events are supplementary, so failing to get them shouldn't stop the case loading.
			console.error(err)
		}
	}

	public async createNew (isCase: boolean) {
		if (this.creating || !this.currentCase) return

		this.creating = true
		let caseWasCreated = false

		try {
			this.currentCase.isCase = isCase
			const created = await api.saveCase(this.currentCase)
			caseWasCreated = true

			if (this.fileService.uploadFiles.length === 0) {
				if (this.caseNumber) {
					this.$emit("caseCreated", created)
				} else {
					await this.$router.replace({ name: "case", params: { caseNumber: created?.caseNumber ?? "" } })
				}

				this.creating = false
			} else {
				this.createdCaseNumber = created.caseNumber
				await this.fileService.uploadPendingFiles(created.id!)
			}
		} catch (error) {
			if (!caseWasCreated) {
				this.displayCreateError(error, isCase)
			}
			console.error(error)
			this.creating = false
		}
	}

	private displayCreateError (error: unknown, isCase: boolean) {
		const recordType = isCase ? this.caseName : this.recordName
		const createError = error as any

		if (Array.isArray(createError?.errors) && createError.errors.length > 0) {
			createError.errors.forEach((e: any) => {
				const fieldTitle = e?.field?.title
				const message = e?.message || "Unknown validation error"
				const body = fieldTitle
					? `There was a problem adding the field ${fieldTitle}: ${message}`
					: `There was a problem creating the ${recordType}: ${message}`

				this.$bvToast.toast(body, {
					title: fieldTitle ? `Error adding field ${fieldTitle}` : `Failed to create ${recordType}`,
					variant: "danger",
					solid: true,
					autoHideDelay: 10000000
				})
			})
			return
		}

		const errorMessage = typeof createError === "string"
			? createError
			: createError?.error || createError?.message || `Unable to create ${recordType}.`

		this.$bvToast.toast(errorMessage, {
			title: `Failed to create ${recordType}`,
			variant: "danger",
			solid: true,
			autoHideDelay: 10000000
		})
	}

	async closeCase () {
		if (this.currentCase) {
			if (this.closingCase) return

			this.closingCase = true
			try {
				await api.close(this.currentCase.id!)
			} catch (exception) {
				const missingFieldErrorPrefix = `Missing ${this.currentCase.isCase ? this.caseName : this.recordName} Field values - `
				const missingActionErrorPrefix = `Missing ${this.currentCase.isCase ? this.caseName : this.recordName} Actions - `
				let exceptionString = (exception as any).error as string
				if (exceptionString && exceptionString.length && exceptionString.startsWith(missingFieldErrorPrefix)) {
					// Failed due to missing fields
					let missingFieldsListString = this.getTitlesFromErrorMessage(exceptionString.substring(missingFieldErrorPrefix.length).trim())

					this.$bvToast.toast(`The case could not be closed because the following field(s) must be set: ${missingFieldsListString}`, {
						title: `Failed to close ${this.currentCase.isCase ? this.caseName : this.recordName}`,
						variant: "danger",
						solid: true,
						autoHideDelay: 10000000
					})
				} else if (exceptionString && exceptionString.length && exceptionString.startsWith(missingActionErrorPrefix)) {
					// Failed due to missing actions
					let missingActionsListString = this.getTitlesFromErrorMessage(exceptionString.substring(missingActionErrorPrefix.length).trim())

					this.$bvToast.toast(`The case could not be closed because the following field(s) must be set: ${missingActionsListString}`, {
						title: `Failed to close ${this.currentCase.isCase ? this.caseName : this.recordName}`,
						variant: "danger",
						solid: true,
						autoHideDelay: 10000000
					})
				} else {
					this.$bvToast.toast(exception as string, {
						title: `Failed to close ${this.currentCase.isCase ? this.caseName : this.recordName}`,
						variant: "danger",
						solid: true,
						autoHideDelay: 10000000
					})
				}
			} finally {
				this.closingCase = false
			}
		}
	}

	async redirectAfterClose () {
		if (this.currentCase && this.currentCase.isClosed) {
			await this.$router.push("/cases")
		}
	}

	public async markSensitive () {
		if (this.currentCase?.id) {
			try {
				await api.markAsSensitive(this.currentCase.id)
				this.currentCase.isSensitive = true
			} catch (err) {
				console.log(err)
			}
		}
	}

	public async promoteToCase () {
		if (!this.currentCase?.id) {
			return
		}
		await api.recordToCase(this.currentCase.id)
	}

	private get lastUpdated (): string {
		if (this.currentCase && this.currentCase.id && this.currentCase.lastUpdated) {
			return this.currentCase.lastUpdated
		}
		return ""
	}

	/**
	 * Extracts titles from Id Title pairs in error message for CloseCase api call and returns a the titles as an array
	 * @param errorMessageString: Error message string of format "(Id: abc, Title: xyz) (Id: def, Title: uvw)"
	 */
	private getTitlesFromErrorMessage (errorMessageString: string): string[] {
		// Extract all Title values
		const titleRegex = /Title:\s*([^)]*)/g // Matches "Title: [Field Title]" within each field
		const matches = []
		let match

		while ((match = titleRegex.exec(errorMessageString)) !== null) {
			matches.push(match[1].trim()) // Add the extracted Title to the list
		}

		return matches
	}
}
